/**
 * Relations API
 */

import { api, type ApiResponse, type ApiListResponse } from '@/lib/api';

// ============== Types ==============

export interface RelationSchema {
    constraint_name: string | null;
    table: string;
    column: string;
    foreign_key_table: string;
    foreign_key_column: string;
    on_update?: string;
    on_delete?: string;
}

export interface RelationMeta {
    id?: number;
    many_collection: string;
    many_field: string;
    one_collection: string | null;
    one_field: string | null;
    one_collection_field: string | null;
    one_allowed_collections: string[] | null;
    junction_field: string | null;
    sort_field: string | null;
    one_deselect_action: 'nullify' | 'delete';
}

export interface Relation {
    collection: string;
    field: string;
    related_collection: string | null;
    schema: RelationSchema | null;
    meta: RelationMeta | null;
}

export interface CreateRelationPayload {
    collection: string;
    field: string;
    related_collection?: string | null;
    schema?: {
        on_delete?: 'NO ACTION' | 'SET NULL' | 'SET DEFAULT' | 'CASCADE' | 'RESTRICT';
        on_update?: 'NO ACTION' | 'SET NULL' | 'SET DEFAULT' | 'CASCADE' | 'RESTRICT';
    } | null;
    meta?: Partial<RelationMeta>;
}

export interface UpdateRelationPayload {
    related_collection?: string | null;
    schema?: {
        on_delete?: 'NO ACTION' | 'SET NULL' | 'SET DEFAULT' | 'CASCADE' | 'RESTRICT';
        on_update?: 'NO ACTION' | 'SET NULL' | 'SET DEFAULT' | 'CASCADE' | 'RESTRICT';
    } | null;
    meta?: Partial<RelationMeta>;
}

// Relation types
export const RELATION_TYPES = [
    { value: 'm2o', label: 'Many to One', description: 'Link many items to one related item' },
    { value: 'o2m', label: 'One to Many', description: 'Link one item to many related items' },
    { value: 'm2m', label: 'Many to Many', description: 'Link many items to many related items' },
    { value: 'm2a', label: 'Many to Any', description: 'Link many items to items from multiple collections' },
] as const;

// On delete/update actions
export const REFERENTIAL_ACTIONS = [
    { value: 'NO ACTION', label: 'No Action' },
    { value: 'SET NULL', label: 'Set NULL' },
    { value: 'SET DEFAULT', label: 'Set Default' },
    { value: 'CASCADE', label: 'Cascade' },
    { value: 'RESTRICT', label: 'Restrict' },
] as const;

// ============== API Functions ==============

export async function getRelations(collection?: string): Promise<Relation[]> {
    const url = collection ? `/relations/${collection}` : '/relations';
    const response = await api.get<ApiListResponse<Relation>>(url);
    return response.data ?? [];
}

export async function getRelationsForCollection(collection: string): Promise<Relation[]> {
    const response = await api.get<ApiListResponse<Relation>>(`/relations/${collection}`);
    return response.data ?? [];
}

export async function getRelation(collection: string, field: string): Promise<Relation> {
    const response = await api.get<ApiResponse<Relation>>(`/relations/${collection}/${field}`);
    return response.data;
}

export async function createRelation(payload: CreateRelationPayload): Promise<Relation> {
    const response = await api.post<ApiResponse<Relation>>('/relations', payload);
    return response.data;
}

export async function updateRelation(
    collection: string,
    field: string,
    payload: UpdateRelationPayload
): Promise<Relation> {
    const response = await api.patch<ApiResponse<Relation>>(`/relations/${collection}/${field}`, payload);
    return response.data;
}

export async function deleteRelation(collection: string, field: string): Promise<void> {
    await api.delete(`/relations/${collection}/${field}`);
}
